import type { AmountMode, Occurrence } from '@klopotun/contracts';
import { formatCivilDate, fromMinorUnits, toMinorUnits, type CivilDate } from '@klopotun/domain';
import type { ConfirmPaymentValues, PayBodyDraft } from '../model/confirm-payment-values.js';

/**
 * `toMinorUnits` refuses anything that is not a plain decimal, and a half typed amount is exactly
 * that. The exponent belongs to the currency - "299.00" is 29900 in UAH and 299 in JPY - which is
 * why the arithmetic stays in packages/domain instead of a multiplication by a hundred here.
 */
export const toAmountMinor = (amount: string, currency: string): number | null => {
  try {
    return toMinorUnits(amount.trim().replace(',', '.'), currency);
  } catch {
    return null;
  }
};

const currencyOf = (occurrence: Occurrence): string =>
  occurrence.paidCurrency ?? occurrence.expectedCurrency;

const initialAmountOf = (occurrence: Occurrence): string => {
  const { paidAmountMinor, paidCurrency } = occurrence;

  if (paidAmountMinor !== null && paidCurrency !== null) {
    return fromMinorUnits(paidAmountMinor, paidCurrency);
  }

  if (occurrence.expectedAmountMinor === null) {
    return '';
  }

  return fromMinorUnits(occurrence.expectedAmountMinor, occurrence.expectedCurrency);
};

/**
 * The same form confirms a period for the first time and corrects one already confirmed (design
 * note C12), so an amount and a date already on record win over the expected ones.
 */
export const toConfirmPaymentValues = (
  occurrence: Occurrence,
  today: CivilDate,
): ConfirmPaymentValues => ({
  amount: initialAmountOf(occurrence),
  paidOn: occurrence.paidOn ?? formatCivilDate(today),
  applyAmountTo: 'once',
  currency: currencyOf(occurrence),
});

export const toPayBodyDraft = (values: ConfirmPaymentValues): PayBodyDraft => ({
  paidOn: values.paidOn.trim(),
  paidAmountMinor: toAmountMinor(values.amount, values.currency),
  paidCurrency: values.currency,
  applyAmountTo: values.applyAmountTo,
});

export type AmountQuestionInput = {
  amountMode: AmountMode;
  templateAmountMinor: number | null;
  templateCurrency: string;
  amountMinor: number | null;
  currency: string;
};

/**
 * Design note C5a: "just this once or from now on" is asked of a fixed amount that has changed
 * and of nothing else. A payment with a variable amount has no expected amount to carry forward,
 * so for it the question has no meaning at all.
 *
 * The comparison is against the TEMPLATE, never against the period, because that is what the
 * server compares (`carryAmountForward` in occurrences.service.ts). Asking on any other grounds
 * offers a choice that is then silently ignored - correcting a period confirmed before a price
 * change would ask it for nothing, and a period settled in another currency would ask it while
 * the server refuses to move a template it cannot convert without a rate (design note C12).
 */
export const isAmountQuestionAsked = (input: AmountQuestionInput): boolean => {
  const isFixedAmount = input.amountMode === 'fixed';
  const isSameCurrency = isFixedAmount && input.currency === input.templateCurrency;
  const hasBothAmounts =
    isSameCurrency && input.amountMinor !== null && input.templateAmountMinor !== null;
  const hasChangedAmount = hasBothAmounts && input.amountMinor !== input.templateAmountMinor;

  return hasChangedAmount;
};
